/**
 * Responsive Utilities
 * Helper functions and constants for converting hardcoded styles to responsive patterns
 */

import React from 'react'

// Breakpoint constants matching Tailwind CSS defaults
export const BREAKPOINTS = {
  xs: '0px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px'
} as const

// Common responsive class mappings
export const RESPONSIVE_CLASSES = {
  // Container patterns
  container: {
    hardcoded: 'width: 1920px; margin-left: 280px;',
    responsive: 'w-full max-w-screen-2xl mx-auto px-4 lg:px-8'
  },

  // Card patterns
  card: {
    hardcoded: 'width: 253px; height: 140px;',
    responsive: 'w-full md:w-auto md:min-w-[253px] h-auto md:h-[140px]'
  },

  // Text sizing
  text: {
    small: 'text-sm md:text-base',
    base: 'text-base md:text-lg',
    large: 'text-lg md:text-xl lg:text-2xl'
  },

  // Spacing patterns
  spacing: {
    small: 'p-2 md:p-3 lg:p-4',
    medium: 'p-4 md:p-6 lg:p-8',
    large: 'p-6 md:p-8 lg:p-12'
  }
} as const

/**
 * Convert hardcoded pixel width to responsive classes
 */
export function convertWidth(pixels: number): string {
  if (pixels <= 320) return 'w-full max-w-xs'
  if (pixels <= 400) return 'w-full max-w-sm'
  if (pixels <= 600) return 'w-full max-w-md'
  if (pixels <= 800) return 'w-full max-w-lg'
  if (pixels <= 1024) return 'w-full max-w-xl'
  if (pixels <= 1280) return 'w-full max-w-2xl'
  if (pixels <= 1536) return 'w-full max-w-4xl'
  return 'w-full max-w-screen-2xl'
}

/**
 * Convert hardcoded pixel height to responsive classes
 */
export function convertHeight(pixels: number): string {
  if (pixels <= 100) return 'h-auto min-h-[100px]'
  if (pixels <= 200) return 'h-auto min-h-[200px] md:h-auto'
  if (pixels <= 400) return 'h-auto md:h-[400px]'
  return 'h-auto md:h-screen'
}

/**
 * Convert hardcoded margins to responsive spacing
 */
export function convertMargin(pixels: number): string {
  if (pixels <= 8) return 'm-1 md:m-2'
  if (pixels <= 16) return 'm-2 md:m-4'
  if (pixels <= 24) return 'm-3 md:m-6'
  if (pixels <= 32) return 'm-4 md:m-8'
  if (pixels <= 48) return 'm-6 md:m-12'
  return 'm-8 md:m-16'
}

/**
 * Convert hardcoded padding to responsive spacing
 */
export function convertPadding(pixels: number): string {
  if (pixels <= 8) return 'p-1 md:p-2'
  if (pixels <= 16) return 'p-2 md:p-4'
  if (pixels <= 24) return 'p-3 md:p-6'
  if (pixels <= 32) return 'p-4 md:p-8'
  if (pixels <= 48) return 'p-6 md:p-12'
  return 'p-8 md:p-16'
}

/**
 * Generate responsive grid classes based on item count and min width
 */
export function generateGridClasses(
  minItemWidth: number = 253,
  maxColumns: number = 4
): string {
  return `
    grid
    grid-cols-1
    md:grid-cols-2
    lg:grid-cols-${Math.min(maxColumns, 3)}
    xl:grid-cols-[repeat(auto-fit,minmax(${minItemWidth}px,1fr))]
    gap-4 md:gap-6 lg:gap-8
  `.trim().replace(/\s+/g, ' ')
}

/**
 * Convert style object with hardcoded pixels to responsive classes
 */
export function convertStyleToResponsive(style: React.CSSProperties): string {
  const classes: string[] = []

  // Convert width
  if (style.width && typeof style.width === 'string') {
    const pixels = parseInt(style.width.replace('px', ''))
    if (!isNaN(pixels)) {
      classes.push(convertWidth(pixels))
    }
  }

  // Convert height
  if (style.height && typeof style.height === 'string') {
    const pixels = parseInt(style.height.replace('px', ''))
    if (!isNaN(pixels)) {
      classes.push(convertHeight(pixels))
    }
  }

  // Convert margin
  if (style.margin && typeof style.margin === 'string') {
    const pixels = parseInt(style.margin.replace('px', ''))
    if (!isNaN(pixels)) {
      classes.push(convertMargin(pixels))
    }
  }

  // Convert padding
  if (style.padding && typeof style.padding === 'string') {
    const pixels = parseInt(style.padding.replace('px', ''))
    if (!isNaN(pixels)) {
      classes.push(convertPadding(pixels))
    }
  }

  return classes.join(' ')
}

/**
 * Responsive typography scale based on screen size
 */
export const TYPOGRAPHY_SCALE = {
  xs: 'text-xs',           // 12px
  sm: 'text-sm',           // 14px
  base: 'text-base',       // 16px
  lg: 'text-lg',           // 18px
  xl: 'text-xl',           // 20px
  '2xl': 'text-2xl',       // 24px
  '3xl': 'text-3xl',       // 30px

  // Responsive combinations
  'sm-responsive': 'text-xs md:text-sm',
  'base-responsive': 'text-sm md:text-base',
  'lg-responsive': 'text-base md:text-lg',
  'xl-responsive': 'text-lg md:text-xl',
  '2xl-responsive': 'text-xl md:text-2xl',
} as const

/**
 * Touch-friendly sizing utilities
 */
export const TOUCH_TARGETS = {
  minimum: 'min-h-[44px] min-w-[44px]',
  comfortable: 'min-h-[48px] min-w-[48px]',
  large: 'min-h-[56px] min-w-[56px]'
} as const

/**
 * Common responsive layout patterns
 */
export const LAYOUT_PATTERNS = {
  // Sidebar layout
  sidebar: {
    mobile: 'flex flex-col',
    desktop: 'flex flex-row'
  },

  // Card grid
  cardGrid: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6',

  // Stack on mobile, row on desktop
  responsiveStack: 'flex flex-col md:flex-row gap-4 md:gap-6',

  // Center content
  center: 'flex items-center justify-center',

  // Full height container
  fullHeight: 'min-h-screen flex flex-col'
} as const

/**
 * Media query hook for JavaScript logic
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = React.useState(false)

  React.useEffect(() => {
    const media = window.matchMedia(query)
    if (media.matches !== matches) {
      setMatches(media.matches)
    }

    const listener = () => setMatches(media.matches)
    media.addEventListener('change', listener)
    return () => media.removeEventListener('change', listener)
  }, [matches, query])

  return matches
}

// Common media queries
export const MEDIA_QUERIES = {
  mobile: '(max-width: 767px)',
  tablet: '(min-width: 768px) and (max-width: 1023px)',
  desktop: '(min-width: 1024px)',
  large: '(min-width: 1280px)',
  touch: '(pointer: coarse)'
} as const

/**
 * Check if device is touch-enabled
 */
export function isTouchDevice(): boolean {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0
}

/**
 * Get current breakpoint
 */
export function getCurrentBreakpoint(): keyof typeof BREAKPOINTS {
  const width = window.innerWidth

  if (width < 640) return 'xs'
  if (width < 768) return 'sm'
  if (width < 1024) return 'md'
  if (width < 1280) return 'lg'
  if (width < 1536) return 'xl'
  return '2xl'
}

export default {
  BREAKPOINTS,
  RESPONSIVE_CLASSES,
  convertWidth,
  convertHeight,
  convertMargin,
  convertPadding,
  generateGridClasses,
  convertStyleToResponsive,
  TYPOGRAPHY_SCALE,
  TOUCH_TARGETS,
  LAYOUT_PATTERNS,
  useMediaQuery,
  MEDIA_QUERIES,
  isTouchDevice,
  getCurrentBreakpoint
}